"use client"

import { motion } from 'framer-motion'
import { MapPin, Navigation, CalendarPlus, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'

type Location = {
  name: string
  address: string
  hours: string
  details?: string[]
  businessName?: string
  coordinates?: string // Format: "lat,lng"
  placeId?: string // Google Place ID
}

const getMapboxStaticImageUrl = (coordinates: string, width: number = 1200, height: number = 320) => {
  // Parse coordinates (format: "lat,lng")
  const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()))
  
  // Get Mapbox access token
  const env = import.meta.env as { VITE_MAPBOX_TOKEN?: string; VITE_MAPBOX_ACCESS_TOKEN?: string }
  const accessToken = env.VITE_MAPBOX_TOKEN || env.VITE_MAPBOX_ACCESS_TOKEN
  
  if (!accessToken) {
    console.warn('Mapbox token not found for static images')
    // Fallback to a placeholder or empty string
    return ''
  }
  
  // Mapbox Static Images API
  // Format: https://api.mapbox.com/styles/v1/{username}/{style_id}/static/{overlay}/{lon},{lat},{zoom}/{width}x{height}{@2x}?access_token={your_access_token}
  const styleId = 'streets-v12' // Streets style shows more labels, landmarks, and POIs
  const username = 'mapbox'
  const zoom = 13.5 // Slightly zoomed out to show more context and landmarks
  
  // Use larger pin (pin-l) with bright red color (ff3333)
  // Note: Label removed as it wasn't displaying reliably
  const pinColor = 'ff3333' // Bright red
  const marker = `pin-l+${pinColor}(${lng},${lat})`
  
  return `https://api.mapbox.com/styles/v1/${username}/${styleId}/static/${marker}/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${accessToken}`
}

type LocationsSectionProps = {
  locations?: Location[]
  onBook?: (locationName: string) => void
}

const openDirections = (address: string, coordinates?: string, placeId?: string) => {
  const encodedAddress = encodeURIComponent(address)
  
  // Parse coordinates if available
  let coords = ''
  if (coordinates) {
    const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()))
    coords = `${lat},${lng}`
  }
  
  // Detect iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  
  let mapUrl: string
  let googleMapsUrl: string
  
  if (isIOS) {
    // Use coordinates for Apple Maps (more reliable than address)
    mapUrl = coords 
      ? `maps://maps.apple.com/?daddr=${coords}&dirflg=d`
      : `maps://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`
    window.location.href = mapUrl
    
    // Build Google Maps fallback URL
    if (placeId) {
      // Use place URL format which opens the business listing page
      googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${placeId}`
    } else {
      googleMapsUrl = coords 
        ? `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`
    }
    
    setTimeout(() => {
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')
    }, 1000)
  } else {
    // Build Google Maps URL for non-iOS devices
    if (placeId) {
      // Use place URL format which opens the business listing page
      googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${placeId}`
    } else {
      googleMapsUrl = coords
        ? `https://www.google.com/maps/dir/?api=1&destination=${coords}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`
    }
    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')
  }
}

const defaultLocations: Location[] = [
  {
    name: 'Noosa',
    address: '31 Burgess Drive, Tewantin QLD 4565',
    hours: 'Open Monday, Wednesday & Friday ~ Saturday by prior arrangement',
    details: [
      'Professional home clinic',
      'Easy street parking out front',
      'Near Parklands Dr; a short drive from Noosa Civic',
      'Serving Tewantin, Noosa, Noosa Heads, Noosaville, Cooroy and the Sunshine Coast',
    ],
    businessName: 'Raintree Chinese Medicine – Noosa',
    coordinates: '-26.406336265416424, 153.0262562704724', // Noosa clinic coordinates
    placeId: 'ChIJEWpOLsNpk2sRInDiJFxvysY', // Google Place ID for Raintree Chinese Medicine & Acupuncture Noosa
  },
  {
    name: 'Maroochydore',
    address: 'Ground Level 5/55 Plaza Parade, Maroochydore QLD 4558',
    hours: 'Open Tuesday and Thursday',
    details: [
      'Kon-Tiki Building',
      'Easy off street parking',
      'Opposite Marrochydore Plaza',
      'Serving Marrochydore, Buderim, Bli Bli, Cotton Tree, Mooloolaba and the Sunshine Coast',
    ],
    businessName: 'Raintree Chinese Medicine – Maroochydore',
    coordinates: '-26.657529591547217, 153.08437335189674', // Maroochydore clinic coordinates
  },
]

export function LocationsSection({ locations = defaultLocations, onBook }: LocationsSectionProps) {
  return (
    <section id="locations-section" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl md:text-3xl mb-6 text-[#3f4141]">Clinic Locations</h2>
          <h3 className="text-lg md:text-lg mb-2 font-normal text-gray-600 max-w-2xl mx-auto font-light">Two Clinics, One Style of Care</h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            You can see Renee at either clinic, depending on what’s easiest for you.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          {locations.map((location, index) => (
            <motion.div
              key={location.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden flex flex-col"
            >
              {/* Map thumbnail header */}
              <div className="w-full h-40 overflow-hidden relative bg-gray-200">
                {location.coordinates ? (
                  <img
                    src={getMapboxStaticImageUrl(location.coordinates, 1200, 320)}
                    alt={`Map showing location of ${location.name}`}
                    className="w-full h-full object-cover"
                    style={{ filter: 'grayscale(50%) contrast(1.1)' }}
                    loading="lazy"
                    onError={(e) => {
                      // Fallback if image fails to load
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <MapPin className="h-8 w-8" />
                  </div>
                )}
                {/* Get Directions button overlay */}
                <button
                  onClick={() => openDirections(location.address, location.coordinates, location.placeId)}
                  className="absolute bottom-2 right-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-md text-xs font-medium shadow-lg flex items-center gap-1.5 transition-colors duration-200 z-10"
                  aria-label={`Get directions to ${location.name}`}
                >
                  <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Get Directions</span>
                </button>
              </div>
              <div className="p-6 flex flex-col">
                <div className="flex items-start gap-3 mb-2 min-h-[100px] md:min-h-[110px]">
                <MapPin className="h-6 w-6 text-[#a48b61] flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-xl font-normal text-[#3f4141] mb-2">{location.name}</h3>
                  <div className="min-h-[20px] mb-2">
                    {location.businessName && (
                      <p className="text-gray-600 text-sm font-medium">{location.businessName}</p>
                    )}
                  </div>
                  <p className="text-gray-700 font-medium">{location.address}</p>
                </div>
              </div>
              <hr className="border-gray-300 mx-6 my-0" />
              <div className="px-6 pt-2 min-h-[120px] md:min-h-[140px] flex flex-col">
                <p className="text-gray-500 font-semibold text-sm mb-4">{location.hours}</p>
                {/* Book Appointment button */}
                <div className="w-full sm:w-2/3 md:w-full mx-auto mb-4 mt-auto">
                  <Button
                    variant="outline"
                    onClick={() => onBook?.(location.name)}
                    className="w-full border-gray-300 text-gray-700 bg-white/40 hover:bg-white/80 px-3 py-2.5 min-h-[44px]"
                  >
                    <span className="inline-flex items-center gap-1.5 sm:gap-2 justify-center flex-wrap">
                      <CalendarPlus className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" aria-hidden="true" />
                      <span className="text-center break-words">Book Appointment - {location.name}</span>
                    </span>
                  </Button>
                </div>
              </div>
              {location.details && location.details.length > 0 && (
                <div className="px-6">
                  <ul className="space-y-2 text-sm text-gray-600 pl-0 pt-4">
                    {location.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-[#a48b61] mt-1">•</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="px-6 pb-6 pt-4 mt-auto">
                <Link
                  to={location.name === 'Noosa' ? '/clinics/noosa' : '/clinics/maroochydore'}
                  className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-sm"
                >
                  Learn more about Raintree {location.name === 'Noosa' ? 'Noosa' : location.name} clinic
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

